import React, { useState } from 'react';
import Flex from '../../../../components/Flex';
import { makePrioStyles } from '../../../../theme/utils';
import NavigationBar from '../../../../components/NavigationBar';
import { useTranslation } from 'react-i18next';
import ContactSearch from '../../../contacts/components/ContactSearch';
import {
  Button,
  Dropdown,
  Menu,
  useTheme,
} from '@prio365/prio365-react-library';
import PersonnelFile from './PersonnelFile';
import {
  Route,
  Routes as ReactRouterDomRoutes,
  useParams,
} from 'react-router-dom';
import { AddEmployeeDrawer } from '../AddEmployeeDrawer';
import { useNavigate } from 'react-router-dom';
import { OfficeId } from '../../../../models/Types';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';
import { PrioTheme } from '../../../../theme/types';
import MenuItem from '@prio365/prio365-react-library/lib/Menu/components/MenuItem';
import ContactSearchInputField from '../../../contacts/components/ContactSearchInputField';

const Routes = Sentry.withSentryReactRouterV6Routing(ReactRouterDomRoutes);

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  root: {
    overflow: 'hidden',
    maxHeight: '100%',
  },
  menu: {
    flex: 1,
    maxWidth: theme.old.components.menuMaxWidth,
  },
  content: {
    height: '70%',
    overflow: 'hidden',
  },
  columnSearchContent: {
    borderRight: theme.old.borders.sub,
    width: 320,
  },
  searchContainer: {
    padding: `${theme.spacing.regular}px ${theme.spacing.small}px ${theme.spacing.regular}px 24px`,
  },
  contactList: {
    flex: 1,
    borderTop: theme.old.borders.content,
  },
  contactListItem: {
    '& .ant-list-item-action': {
      visibility: 'hidden',
    },
    '&:hover': {
      backgroundColor: theme.old.palette.backgroundPalette.hover.content,
      '& .ant-list-item-action': {
        visibility: 'visible',
      },
    },
    minHeight: 64,
  },
  selectAllCheckbox: {
    marginRight: theme.old.spacing.unit(2),
  },
  personnelFile: {
    flex: 1,
    padding: '24px',
  },
  contextMenu: {
    '& .prio-menu': {
      padding: 0,
      fontSize: theme.font.fontSize.small,
    },
    '& .prio-menu-item': {
      marginLeft: 0,
    },
  },
}));

interface PersonnelFilesProps {
  className?: string;
  officeId?: OfficeId;
  onlyShowTabs?: string[];
  hideNavBar?: boolean;
}

export const PersonnelFiles: React.FC<PersonnelFilesProps> = (props) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const { className, hideNavBar, onlyShowTabs } = props;
  const { officeId } = useParams();
  const theme = useTheme<PrioTheme>();
  const navigate = useNavigate();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const [searchInput, setSearchInput] = React.useState<string>('');

  const [addEmployeeDrawerOpen, setAddEmployeeDrawerOpen] =
    useState<boolean>(false);
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const [includeArchived, setIncludeArchived] = useState<boolean>(false);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const onSearchInputChange = (value: string) => {
    setSearchInput(value);
  };

  const openAddEmployeeDrawer = () => setAddEmployeeDrawerOpen(true);
  const closeAddEmployeeDrawer = () => setAddEmployeeDrawerOpen(false);
  //#endregion

  //#region ------------------------------ Components
  const menu = () => (
    <Menu selectedItemId={includeArchived ? '1' : undefined}>
      <MenuItem
        key="1"
        id="1"
        label={
          includeArchived
            ? t('contacts:contactSelectionList.contextMenu.hideArchived')
            : t('contacts:contactSelectionList.contextMenu.showArchived')
        }
        onClick={() => {
          setIncludeArchived(!includeArchived);
        }}
      ></MenuItem>
    </Menu>
  );
  //#endregion

  return (
    <>
      <Flex.Column className={classNames(classes.root, className)}>
        {!hideNavBar && (
          <NavigationBar>
            <Button
              onClick={openAddEmployeeDrawer}
              iconProp={['fal', 'user-plus']}
            >
              <span>{t('hr:personnelFile.navigationBar.newEmployee')}</span>
            </Button>
          </NavigationBar>
        )}
        <Flex.Row className={classes.content} flex={1}>
          <Flex.Column className={classes.columnSearchContent}>
            <Flex.Row
              className={classes.searchContainer}
              alignItems="center"
              childrenGap={theme.spacing.small}
            >
              <ContactSearchInputField
                onChange={onSearchInputChange}
                isOnline={isOnline}
              />
              <Dropdown
                overlay={menu()}
                overlayClassName={classes.contextMenu}
                placement="bottomRight"
              >
                <Button
                  iconProp={['fal', 'ellipsis-v']}
                  type={includeArchived ? 'primary' : 'default'}
                />
              </Dropdown>
            </Flex.Row>
            <ContactSearch
              type="allInternalContactsInOffice"
              searchTerm={searchInput}
              onContactClick={(contactId) => navigate(contactId)}
              className={classes.contactList}
              officeId={officeId}
              searchTypeChanged={setIsOnline}
              includeArchived={includeArchived}
            />
          </Flex.Column>
          <Routes>
            <Route
              path=":employeeId"
              element={
                <PersonnelFile
                  className={classes.personnelFile}
                  officeId={officeId}
                  pathPrefix={'..'}
                  onlyShowTabs={onlyShowTabs}
                />
              }
            />
          </Routes>
        </Flex.Row>
      </Flex.Column>
      <AddEmployeeDrawer
        isDrawerOpen={addEmployeeDrawerOpen}
        onDrawerClose={closeAddEmployeeDrawer}
      />
    </>
  );
};

export default PersonnelFiles;
